import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGazeConfig } from './GazeConfigContext';
import './LandingPage.css';

function LandingPage() {
  const navigate                       = useNavigate();
  const { calibration, config, updateConfig } = useGazeConfig();

  function handleReadAloudToggle() {
    const next = !config.readAloud;
    if (next && window.speechSynthesis) {
      // Chrome requires a real audible utterance inside a gesture to unlock
      // subsequent programmatic speech. Speak the confirmation phrase here.
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance('Read aloud on');
      u.rate = 0.82; u.pitch = 1.0; u.volume = 1.0;
      if (config.voiceURI) {
        const voice = window.speechSynthesis.getVoices().find(v => v.voiceURI === config.voiceURI);
        if (voice) u.voice = voice;
      }
      window.speechSynthesis.speak(u);
    } else if (!next && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    updateConfig({ readAloud: next });
  }

  // ── Voice picker ─────────────────────────────────────────────────────────
  const AAC_BLOCKLIST = new Set([
    'Bahh', 'Bells', 'Boing', 'Bubbles', 'Cellos', 'Cello',
    'Deranged', 'Good News', 'Hysterical', 'Organ', 'Pipe Organ',
    'Princess', 'Trinoids', 'Whisper', 'Wobble', 'Zarvox', 'Bad News',
    'Albert', 'Fred', 'Junior', 'Kathy', 'Ralph',
    'Eddy', 'Flo', 'Jester', 'Reed', 'Rocko', 'Sandy', 'Shelley',
    'Superstar', 'Bruce',
  ]);

  const [voices, setVoices] = useState([]);
  useEffect(() => {
    function load() {
      const all = window.speechSynthesis?.getVoices() ?? [];
      setVoices(
        all
          .filter(v => v.lang.startsWith('en') && !AAC_BLOCKLIST.has(v.name))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }
    load();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = load;
      return () => { window.speechSynthesis.onvoiceschanged = null; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleVoiceChange(uri) {
    updateConfig({ voiceURI: uri });
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const preview = new SpeechSynthesisUtterance('Hello, this is a preview of the selected voice.');
    preview.rate = 0.82; preview.volume = 1.0;
    if (uri) {
      const voice = window.speechSynthesis.getVoices().find(v => v.voiceURI === uri);
      if (voice) preview.voice = voice;
    }
    window.speechSynthesis.speak(preview);
  }

  // If calibration hasn't been done yet for this session, show the calibration
  // screen first, then continue to the intended destination.
  function handleModeSelect(destination) {
    if (!calibration.done) {
      navigate('/calibration', { state: { returnTo: destination } });
    } else {
      navigate(destination);
    }
  }

  return (
    <div className="lp-root">

      {/* ── 1. Header ───────────────────────────────────────────────── */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <span className="lp-eyebrow">Eye2Voice</span>
          <h1 className="lp-headline">
            Gaze-to-speech,{' '}
            <em className="lp-headline-em">simplified.</em>
          </h1>
        </div>
      </header>

      <div className="lp-container">

        {/* ── 2. Mode cards ─────────────────────────────────────────── */}
        <section className="lp-section">
          {/* Quick Chat */}
          <button
            className="lp-mode-card"
            onClick={() => handleModeSelect('/warmup')}
            aria-label="Quick Chat — Guided topic tree"
          >
            <span className="lp-mode-dot lp-mode-dot--primary" />
            <span className="lp-mode-text">
              <span className="lp-mode-name">Quick Chat</span>
              <span className="lp-mode-desc">Guided Conversation Topics</span>
            </span>
          </button>

          {/* Conversation Mode */}
          <button
            className="lp-mode-card"
            onClick={() => handleModeSelect('/conversation/listen')}
            aria-label="Conversation Mode — AI-powered responses from voice"
          >
            <span className="lp-mode-dot lp-mode-dot--accent" />
            <span className="lp-mode-text">
              <span className="lp-mode-name-row">
                <span className="lp-mode-name">Conversation Mode</span>
              </span>
              <span className="lp-mode-desc">AI-powered responses from voice</span>
            </span>
          </button>
        </section>

        {/* ── 3. Action buttons ─────────────────────────────────────── */}
        <div className="lp-settings-row">
          <button
            className="lp-settings-btn"
            onClick={() => navigate('/tips')}
            aria-label="View tips and tricks"
          >
            💡 Tips &amp; Tricks
          </button>
          <button
            className="lp-settings-btn"
            onClick={() => navigate('/config')}
            aria-label="Open settings"
          >
            ⚙ Adjust Settings
          </button>
        </div>

        {/* ── 4. Read-aloud quick toggle + voice picker ─────────────── */}
        <div className="lp-readloud-row">
          <button
            className={`lp-readloud-btn${config.readAloud ? ' lp-readloud-btn--on' : ''}`}
            onClick={handleReadAloudToggle}
            aria-pressed={config.readAloud}
            aria-label={config.readAloud ? 'Read aloud on — tap to turn off' : 'Read aloud off — tap to turn on'}
          >
            🔊 Read Options Aloud
          </button>

          {config.readAloud && voices.length > 0 && (
            <select
              className="lp-voice-select"
              value={config.voiceURI}
              onChange={e => handleVoiceChange(e.target.value)}
              aria-label="Select voice for read aloud"
            >
              <option value="">Default voice</option>
              {voices.map(v => (
                <option key={v.voiceURI} value={v.voiceURI}>{v.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* ── 5. How it works ───────────────────────────────────────── */}
        <section className="lp-section">
          <p className="lp-section-label">How it works</p>
          <div className="lp-features">

            {/* Feature 1 — eye */}
            <div className="lp-feature-row">
              <span className="lp-icon-tile" aria-hidden="true">
                <svg viewBox="0 0 8 8" width="20" height="20" fill="none"
                  stroke="#0d5c52" strokeWidth="1">
                  <circle cx="4" cy="4" r="3" />
                  <circle cx="4" cy="4" r="1.2" />
                </svg>
              </span>
              <span className="lp-feature-text">Look directionally to make screen selections</span>
            </div>

            {/* Feature 2 — swipe up */}
            <div className="lp-feature-row">
              <span className="lp-icon-tile" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <mask id="path-1-outside-1_141_52" maskUnits="userSpaceOnUse" x="9.72461" y="14" width="16" height="16" fill="black">
                    <rect fill="white" x="9.72461" y="14" width="16" height="16"/>
                    <path d="M15.7501 15C16.1642 15 16.5001 15.3358 16.5001 15.75V20.5C16.5001 20.7761 16.7239 21 17.0001 21C17.2762 21 17.5001 20.7761 17.5001 20.5V19.75C17.5001 19.3358 17.8359 19 18.2501 19C18.6642 19 19.0001 19.3358 19.0001 19.75V20.5C19.0001 20.7761 19.2239 21 19.5001 21C19.7762 21 20.0001 20.7761 20.0001 20.5V20.25C20.0001 19.8358 20.3359 19.5 20.7501 19.5C21.1642 19.5 21.5001 19.8358 21.5001 20.25V20.5C21.5001 20.7761 21.7239 21 22.0001 21H22.2501C22.3881 21 22.5001 20.8881 22.5001 20.75C22.5001 20.3358 22.8359 20 23.2501 20C23.6643 20 24.0001 20.3358 24.0001 20.75V24.5C24.0001 26.9853 21.9853 29 19.5001 29C17.9582 29 16.5981 28.2241 15.7872 27.042C14.846 25.9671 13.2031 24.7729 12.1309 23.8926C11.6521 23.4994 11.584 22.768 11.9932 22.3027C12.6974 21.5023 13.8989 22.5875 14.627 23.3662C14.7587 23.507 15.0001 23.4161 15.0001 23.2234V15.75C15.0001 15.3358 15.3359 15 15.7501 15Z"/>
                  </mask>
                  <path d="M15.7501 15C16.1642 15 16.5001 15.3358 16.5001 15.75V20.5C16.5001 20.7761 16.7239 21 17.0001 21C17.2762 21 17.5001 20.7761 17.5001 20.5V19.75C17.5001 19.3358 17.8359 19 18.2501 19C18.6642 19 19.0001 19.3358 19.0001 19.75V20.5C19.0001 20.7761 19.2239 21 19.5001 21C19.7762 21 20.0001 20.7761 20.0001 20.5V20.25C20.0001 19.8358 20.3359 19.5 20.7501 19.5C21.1642 19.5 21.5001 19.8358 21.5001 20.25V20.5C21.5001 20.7761 21.7239 21 22.0001 21H22.2501C22.3881 21 22.5001 20.8881 22.5001 20.75C22.5001 20.3358 22.8359 20 23.2501 20C23.6643 20 24.0001 20.3358 24.0001 20.75V24.5C24.0001 26.9853 21.9853 29 19.5001 29C17.9582 29 16.5981 28.2241 15.7872 27.042C14.846 25.9671 13.2031 24.7729 12.1309 23.8926C11.6521 23.4994 11.584 22.768 11.9932 22.3027C12.6974 21.5023 13.8989 22.5875 14.627 23.3662C14.7587 23.507 15.0001 23.4161 15.0001 23.2234V15.75C15.0001 15.3358 15.3359 15 15.7501 15Z" fill="white"/>
                  <path d="M15.7501 15L15.7502 14H15.7501V15ZM16.5001 15.75H17.5001V15.75H16.5001ZM18.2501 19L18.2502 18H18.2501V19ZM20.7501 19.5L20.7502 18.5H20.7501V19.5ZM21.5001 20.25H22.5001V20.25H21.5001ZM23.2501 20L23.2501 19H23.2501V20ZM19.5001 29V30H19.5001L19.5001 29ZM15.7872 27.042L16.6118 26.4763L16.5784 26.4277L16.5396 26.3833L15.7872 27.042ZM12.1309 23.8926L11.4963 24.6654L11.4964 24.6654L12.1309 23.8926ZM11.9932 22.3027L11.2424 21.6422L11.2424 21.6422L11.9932 22.3027ZM14.627 23.3662L15.3575 22.6832L15.3575 22.6832L14.627 23.3662ZM15.7501 15L15.75 16C15.6121 16 15.5001 15.8882 15.5001 15.75H16.5001H17.5001C17.5001 14.7834 16.7164 14.0001 15.7502 14L15.7501 15ZM16.5001 15.75H15.5001V20.5H16.5001H17.5001V15.75H16.5001ZM17.5001 20.5H18.5001V19.75H17.5001H16.5001V20.5H17.5001ZM17.5001 19.75H18.5001C18.5001 19.8881 18.3881 20 18.2501 20V19V18C17.2836 18 16.5001 18.7835 16.5001 19.75H17.5001ZM18.2501 19L18.25 20C18.1121 20 18.0001 19.8882 18.0001 19.75H19.0001H20.0001C20.0001 18.7834 19.2164 18.0001 18.2502 18L18.2501 19ZM19.0001 19.75H18.0001V20.5H19.0001H20.0001V19.75H19.0001ZM20.0001 20.5H21.0001V20.25H20.0001H19.0001V20.5H20.0001ZM20.0001 20.25H21.0001C21.0001 20.3881 20.8881 20.5 20.7501 20.5V19.5V18.5C19.7836 18.5 19.0001 19.2835 19.0001 20.25H20.0001ZM20.7501 19.5L20.75 20.5C20.6121 20.5 20.5001 20.3882 20.5001 20.25H21.5001H22.5001C22.5001 19.2834 21.7164 18.5001 20.7502 18.5L20.7501 19.5ZM21.5001 20.25H20.5001V20.5H21.5001H22.5001V20.25H21.5001ZM22.0001 21V22H22.2501V21V20H22.0001V21ZM22.5001 20.75H23.5001C23.5001 20.8881 23.3881 21 23.2501 21V20V19C22.2836 19 21.5001 19.7835 21.5001 20.75H22.5001ZM23.2501 20L23.25 21C23.112 21 23.0001 20.8881 23.0001 20.75H24.0001H25.0001C25.0001 19.7835 24.2165 19 23.2501 19L23.2501 20ZM24.0001 20.75H23.0001V24.5H24.0001H25.0001V20.75H24.0001ZM24.0001 24.5H23.0001C23.0001 26.433 21.4331 28 19.5001 28L19.5001 29L19.5001 30C22.5376 30 25.0001 27.5376 25.0001 24.5H24.0001ZM19.5001 29V28C18.3021 28 17.2444 27.3985 16.6118 26.4763L15.7872 27.042L14.9626 27.6077C15.9518 29.0497 17.6144 30 19.5001 30V29ZM15.7872 27.042L16.5396 26.3833C15.5261 25.2258 13.7512 23.929 12.7655 23.1197L12.1309 23.8926L11.4964 24.6654C12.6551 25.6168 14.1659 26.7083 15.0348 27.7007L15.7872 27.042ZM12.1309 23.8926L12.7656 23.1198C12.7464 23.104 12.7287 23.0759 12.7252 23.0363C12.7217 22.9977 12.7338 22.9749 12.7441 22.9632L11.9932 22.3027L11.2424 21.6422C10.4501 22.543 10.5917 23.9225 11.4963 24.6654L12.1309 23.8926ZM11.9932 22.3027L12.7441 22.9632C12.7464 22.9606 12.7282 22.9812 12.6865 22.9997C12.6451 23.0183 12.6096 23.0218 12.5934 23.0217C12.579 23.0216 12.6461 23.0216 12.8124 23.1147C13.1476 23.3025 13.5415 23.6694 13.8966 24.0492L14.627 23.3662L15.3575 22.6832C14.9844 22.2843 14.4135 21.7192 13.7899 21.3699C13.4768 21.1945 13.066 21.0245 12.6055 21.0217C12.0952 21.0187 11.6112 21.223 11.2424 21.6422L11.9932 22.3027ZM15.0001 23.2234H16.0001V15.75H15.0001H14.0001V23.2234H15.0001ZM15.0001 15.75H16.0001C16.0001 15.8881 15.8881 16 15.7501 16V15V14C14.7836 14 14.0001 14.7835 14.0001 15.75H15.0001ZM19.5001 21V22C20.3285 22 21.0001 21.3284 21.0001 20.5H20.0001H19.0001C19.0001 20.2239 19.2239 20 19.5001 20V21ZM17.0001 21V22C17.8285 22 18.5001 21.3284 18.5001 20.5H17.5001H16.5001C16.5001 20.2239 16.7239 20 17.0001 20V21ZM14.627 23.3662L13.8966 24.0492C14.6166 24.8192 16.0001 24.3688 16.0001 23.2234H15.0001H14.0001C14.0001 22.4634 14.9007 22.1948 15.3575 22.6832L14.627 23.3662ZM21.5001 20.5H20.5001C20.5001 21.3284 21.1716 22 22.0001 22V21V20C22.2762 20 22.5001 20.2239 22.5001 20.5H21.5001ZM19.0001 20.5H18.0001C18.0001 21.3284 18.6716 22 19.5001 22V21V20C19.7762 20 20.0001 20.2239 20.0001 20.5H19.0001ZM22.2501 21V22C22.9404 22 23.5001 21.4404 23.5001 20.75H22.5001H21.5001C21.5001 20.3358 21.8359 20 22.2501 20V21ZM16.5001 20.5H15.5001C15.5001 21.3284 16.1716 22 17.0001 22V21V20C17.2762 20 17.5001 20.2239 17.5001 20.5H16.5001Z" fill="#535353" mask="url(#path-1-outside-1_141_52)"/>
                  <path d="M5.35355 9.64645C5.15829 9.45118 4.84171 9.45118 4.64645 9.64645L1.46447 12.8284C1.2692 13.0237 1.2692 13.3403 1.46447 13.5355C1.65973 13.7308 1.97631 13.7308 2.17157 13.5355L5 10.7071L7.82843 13.5355C8.02369 13.7308 8.34027 13.7308 8.53553 13.5355C8.7308 13.3403 8.7308 13.0237 8.53553 12.8284L5.35355 9.64645ZM5 18H5.5L5.5 10H5H4.5L4.5 18H5Z" fill="black"/>
                </svg>
              </span>
              <span className="lp-feature-text">Swipe up to go back to topics</span>
            </div>

            {/* Feature 3 — up arrow */}
            <div className="lp-feature-row">
              <span className="lp-icon-tile" aria-hidden="true">
                <svg viewBox="0 0 8 8" width="20" height="20" fill="none"
                  stroke="#0d5c52" strokeWidth="1">
                  <polyline points="2,5 4,2 6,5" />
                  <line x1="4" y1="2" x2="4" y2="7" />
                </svg>
              </span>
              <span className="lp-feature-text">Hold gaze upward to go back</span>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
}

export default LandingPage;
